<template>
  <CCard>
    <CCardHeader class="d-flex justify-content-end align-items-center">
      <h2 class="text-uppercase font-weight-bold mr-auto">Enterprise Users</h2>
      <CButton
        v-if="$auth.check('instructors_create_invite')"
        class="ml-auto mr-3"
        color="primary"
        @click="createUser"
      >
        Add New
      </CButton>
      <CInput
        v-model="filters.search"
        class="mb-0"
        placeholder="Search"
        @update:value="searchChange"
      />
    </CCardHeader>
    <CCardBody>
      <CDataTable
        :items="data"
        :fields="c_fields"
        :pagination="false"
        :sorter="{ external: true }"
        :loading="loading"
        class="mb-0"
        :no-items-view="{ noItems: 'No results found' }"
        @update:sorter-value="sortingChange"
      >
        <template #active="data">
          <td>
            {{ data.item.active ? 'Active' : 'Blocked' }}
          </td>
        </template>
        <template #actions="data">
          <td>
            <div class="d-flex">
              <CButton
                size="sm"
                color="info"
                class="text-white mr-2"
                @click="viewUserSubscription(data.item.id)"
              >
                Subscriptions
              </CButton>
              <CDropdown
                color="info"
                size="sm"
                teleport
              >
                <CDropdownToggle color="info" size="sm" class="mr-2 text-white">
                  <span>Actions</span>
                </CDropdownToggle>
                <CDropdownMenu>
                  <CDropdownItem @click="editUser(data.item.id)">Edit</CDropdownItem>

                  <CDropdownItem v-if="data.item.active" @click="blockUser(data.item.id)">
                    Block
                  </CDropdownItem>
                  <CDropdownItem v-else @click="unblockUser(data.item.id)">Unblock</CDropdownItem>
                  <CDropdownItem @click="changePassword(data.item)">Change Password</CDropdownItem>
                  <CDropdownItem
                    v-if="data.item.email && !data.item.email_verified_at"
                    @click="resendVerification(data.item)"
                  >
                    Resend Verification
                  </CDropdownItem>
                  <CDropdownItem
                    v-if="data.item.email && data.item.email_verified_at"
                    @click="resetPassword(data.item)"
                  >
                    Reset Password
                  </CDropdownItem>
                  <CDropdownItem v-if="!data.item.email_verified_at" @click="verify(data.item)">
                    Verify
                  </CDropdownItem>
                  <CDropdownItem @click="auth_admin(data.item)">Login as User</CDropdownItem>
                </CDropdownMenu>
              </CDropdown>
            </div>
          </td>
        </template>
        <template #enter_code="{ item }">
          <td>
            {{ item.enter_code }}
          </td>
        </template>
        <template #active_subscriptions_count="{ item }">
          <td>
            {{ item.active_subscriptions_count || 0 }}
          </td>
        </template>
        <template #active_tablets_count="{ item }">
          <td>
            {{ item.active_tablets_count || 0 }}
          </td>
        </template>
        <template #active_tv_subscriptions_count="{ item }">
          <td>
            {{ item.active_tv_subscriptions_count || 0 }}
          </td>
        </template>
        <template #active_tv_count="{ item }">
          <td>
            {{ item.active_tv_count || 0 }}
          </td>
        </template>
        <template #no-items-view>No users found</template>
      </CDataTable>
      <pagination
        :current-page="options.page"
        :last-page="options.last_page"
        class="mr-3"
        @paginate="pageChange"
      />
    </CCardBody>
    <delete-confirm-modal ref="block_modal" @confirmed="confirmBlockUser" />
    <delete-confirm-modal ref="delete_modal" @confirmed="confirmDeleteUser" />
    <change-password-modal ref="changePasswordModal" />
  </CCard>
</template>

<script>
import externalTable from '@/mixins/externalTable';
import ChangePasswordModal from '@/components/ChangeEnterprisePasswordModal.vue';
import DeleteConfirmModal from './DeleteConfirmModal';

export default {
  name: 'EnterpriseUserList',
  components: { ChangePasswordModal, DeleteConfirmModal },
  mixins: [externalTable],
  data() {
    return {
      subscriptions: false,
      filters: {
        search: '',
        active: [],
      },
      statuses: [
        // {value: null, label: 'All'},
        { value: 1, label: 'Active' },
        { value: 0, label: 'Blocked' },
      ],
      fields: [
        {
          key: 'active',
          label: 'Status',
        },
      ],
      url: '/enterprise-users',
      loadingActionUser: null,
    };
  },
  computed: {
    c_fields() {
      let fields = [
        {
          key: 'name',
          label: 'Name',
        },
        {
          key: 'email',
          label: 'Email',
        },
        {
          key: 'enter_code',
          label: 'Staff Activation Code',
        },
        {
          key: 'active_subscriptions_count',
          label: 'Tablets Subscriptions',
        },
        {
          key: 'active_tablets_count',
          label: 'Active Tablets',
        },
        {
          key: 'active_tv_subscriptions_count',
          label: 'Tv Subscriptions',
        },
        {
          key: 'active_tv_count',
          label: "Active TV's",
        },
        {
          key: 'active',
          label: 'Status',
        },
      ];
      fields.push({
        key: 'actions',
        label: 'Actions',
        sorter: false,
      });
      return fields;
    },
  },
  mounted() {
    if (localStorage.getItem('current_enterprise_page')) {
      let data = JSON.parse(localStorage.getItem('current_enterprise_page'));
      this.filters.search = data.search;
      this.options.page = data.page;
    }
    this.fetchData();
  },
  beforeUnmount() {
    localStorage.removeItem('current_enterprise_page');
  },
  methods: {
    searchChange() {
      this.options.page = 1;
      this.fetchData();
    },
    getBody() {
      const customBoundary = document.querySelector('#app');

      return window.document.body;
    },
    viewUserSubscription(id) {
      localStorage.setItem(
        'current_enterprise_page',
        JSON.stringify({
          page: this.options.page,
          search: this.filters.search,
        }),
      );
      this.$router.push({ name: 'Enterprise User Subscriptions', params: { id: id } });
    },
    createUser() {
      this.$router.push({ name: 'New Enterprise User' });
    },
    editUser(id) {
      this.$router.push({ name: 'Edit Enterprise User', params: { id: id } });
    },
    changePassword(user) {
      this.$refs.changePasswordModal.open('/enterprise-users/' + user.id + '/change-password');
    },
    blockUser(id) {
      this.$refs.block_modal.open(id);
    },
    confirmBlockUser(id) {
      this.$http.put('/enterprise-users/' + id + '/block').then(() => {
        this.$toast.success('Blocked!');
        this.fetchData();
      });
    },
    unblockUser(id) {
      this.$http.put('/enterprise-users/' + id + '/unblock').then(() => {
        this.$toast.success('Unblocked!');
        this.fetchData();
      });
    },
    confirmDeleteUser(id) {
      this.$http.delete('/enterprise-users/' + id).then(() => {
        this.$toast.success('Deleted!');
        this.fetchData();
      });
    },
    resendVerification(user) {
      this.loadingActionUser = user.id;
      this.$http
        .put('/enterprise-users/' + user.id + '/resend-verification')
        .then(() => {
          this.$toast.success('Email sent!');
        })
        .catch(({ response }) => {
          this.$toast.error(response.data.message);
        })
        .finally(() => {
          this.fetchData();
          this.loadingActionUser = null;
        });
    },
    resetPassword(user) {
      this.loadingActionUser = user.id;
      this.$http
        .put('/enterprise-users/' + user.id + '/reset-password')
        .then(() => {
          this.$toast.success('Email sent!');
        })
        .catch(({ response }) => {
          this.$toast.error(response.data.message);
        })
        .finally(() => {
          this.fetchData();
          this.loadingActionUser = null;
        });
    },
    verify(user) {
      this.loadingActionUser = user.id;
      this.$http
        .put('/enterprise-users/' + user.id + '/verify')
        .then(() => {
          this.$toast.success('Email sent!');
        })
        .catch(({ response }) => {
          this.$toast.error(response.data.message);
        })
        .finally(() => {
          this.fetchData();
          this.loadingActionUser = null;
        });
    },
    auth_admin(user) {
      this.loadingActionUser = user.id;
      this.$http
        .put('/enterprise-users/' + user.id + '/auth')
        .then(({ data }) => {
          this.loadingActionUser = null;
          window.open(data.link, '_blank', 'noreferrer');
        })
        .catch(({ response }) => {
          this.$toast.error(response.data.message);
          this.loadingActionUser = null;
        });
    },
  },
};
</script>

<style scoped>
.fa-google {
  background: conic-gradient(
      from -45deg,
      #ea4335 110deg,
      #4285f4 90deg 180deg,
      #34a853 180deg 270deg,
      #fbbc05 270deg
    )
    73% 55%/150% 150% no-repeat;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
}

:deep(.table-responsive) {
  overflow: visible !important;
}

:deep(.card-body) {
  overflow: visible;
}

:deep(.dropdown-menu[data-coreui-popper]) {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  margin-top: 0.125rem;
}
</style>
